<template>
  <template v-if="editMode">
    <iColumn
      class="message-placeholder-container"
      :width="width"
      :height="height"
      :min-height="minHeight"
      :min-width="minWidth"
      :max-height="maxHeight"
      :max-width="maxWidth"
    >
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
      <div class="medium-message-placeholder" />
    </iColumn>
  </template>
  <template v-else>
    <iColumn
      gap="extraSmall"
      :width="width"
      :height="height"
      :min-height="minHeight"
      :min-width="minWidth"
      :max-height="maxHeight"
      :max-width="maxWidth"
      wrap="nowrap"
    >
      <iRow
        class="section-header"
        gap="extraSmall"
        vertical-align="middle"
        wrap="nowrap"
      >
        <iIcon :icon="getCollapseIcon(isChannelCollapsed)" size="small" @click="toggleCollapse" />
        <iText v-bind="textStyleOverrides">
          Channels
        </iText>
        <iSpace />
        <iButton variant="tertiary" size="extraSmall">
          <iIcon icon="plus" size="small" @click="openCreateChannelModal" />
        </iButton>
      </iRow>

      <iColumn gap="none">
        <iRow v-for="channelData in filteredChannels" :key="channelData.channel_id">
          <iLink
            class="menu-item-link"
            @click="switchActiveEntity(channelData.channel_id, CHANNEL_CONTEXT, channelData.conversation_id)"
          >
            <iRow
              :class="{
                'channel-list-item': true,
                'active': activeChannelId === channelData.channel_id
              }"
              align="between"
              gap="extraSmall"
              vertical-align="middle"
              wrap="nowrap"
            >
              <iRow gap="small" vertical-align="middle" wrap="nowrap">
                <iIcon :icon="channelIcon(channelData.visibility)" size="small" />
                <iText
                  :class="{active: activeChannelId === channelData.channel_id}"
                  :wrap="false"
                  :break-word="false"
                  :font-weight="unseenMessagesMap.get(channelData.conversation_id) ? 'dark' : 'standard'"
                  v-bind="textStyleOverrides"
                >
                  {{ channelData.channel_name.toLowerCase() }}
                </iText>
                <iRow
                  v-if="getActiveParticipantCount(channelData.conversation_id) > 0"
                  wrap="nowrap"
                  width="hug"
                  vertical-align="middle"
                  gap="extraSmall"
                >
                  <iIcon name="flat-color-icons:video-call" size="small" />
                  <iText font-size="small" variant="subtle">
                    {{ getActiveParticipantCount(channelData.conversation_id) }}
                  </iText>
                </iRow>
              </iRow>
              <iChip
                v-if="channelData['num_unread_mentions'] > 0"
                v-tooltip="'You have unread mentions in this channel'"
                :show-icon="false"
                size="small"
                variant="secondary"
                :style-overrides="{
                  secondaryBackgroundColor: 'light'
                }"
              >
                <iText font-size="small">
                  {{ channelData["num_unread_mentions"] }}
                </iText>
              </iChip>
            </iRow>
          </iLink>
        </iRow>
      </iColumn>
      <iRow
        v-if="hasDirectMessages"
        class="section-header"
        gap="extraSmall"
        vertical-align="middle"
        wrap="nowrap"
      >
        <iIcon :icon="getCollapseIcon(isDirectMessagesCollapsed)" size="small" @click="toggleDirectMessagesCollapse" />
        <iText v-bind="textStyleOverrides">
          Direct messages
        </iText>
      </iRow>

      <iColumn v-if="filteredDirectMessageConversations.length > 0" gap="none">
        <iRow
          v-for="directMessageConversation in filteredDirectMessageConversations"
          :key="getDmConversationId(directMessageConversation)"
        >
          <iLink
            class="menu-item-link"
            @click="switchActiveEntity(
              getDmEntityId(directMessageConversation),
              DIRECT_MESSAGE_CONTEXT,
              getDmConversationId(directMessageConversation)
            )"
          >
            <iRow
              :class="{
                'channel-list-item': true,
                'active': activeDirectMessageConversationId === getDmConversationId(directMessageConversation)
              }"
              align="between"
              gap="extraSmall"
              vertical-align="middle"
              wrap="nowrap"
            >
              <iRow gap="small" vertical-align="middle" wrap="nowrap">
                <iImageContainer
                  :alt="getDmUserData(directMessageConversation).username"
                  :height="18"
                  :width="18"
                  :src="getDmUserData(directMessageConversation).avatar_url"
                  class="dm-avatar"
                  shape="rectangle"
                  object-fit="cover"
                />
                <iText
                  :wrap="false"
                  :break-word="false"
                  :font-weight="unseenMessagesMap.get(getDmConversationId(directMessageConversation)) ? 'dark' : 'standard'"
                  v-bind="textStyleOverrides"
                >
                  {{ getDmUserData(directMessageConversation).username }}
                </iText>
                <iRow
                  v-if="getActiveParticipantCount(getDmConversationId(directMessageConversation)) > 0"
                  gap="extraSmall"
                  wrap="nowrap"
                  width="hug"
                  vertical-align="middle"
                >
                  <iIcon name="flat-color-icons:video-call" size="small" />
                  <iText font-size="small" variant="subtle">
                    {{ getActiveParticipantCount(getDmConversationId(directMessageConversation)) }}
                  </iText>
                </iRow>
                <div
                  class="status"
                  :class="authorStatuses[getDmUserData(directMessageConversation).user_id] ? 'online' : 'offline'"
                />
              </iRow>
            </iRow>
          </iLink>
        </iRow>
      </iColumn>
    </iColumn>
    <Teleport to="body">
      <iModal
        :max-width="600"
        :show-primary-action-button="true"
        primary-action-button-label="Update Settings"
        :primary-action-button-loading="loadingUpdateNotificationPreferences"
        :show-secondary-action-button="false"
        :visible="showGlobalNotificationSettingsModel"
        title="Global Notification Settings"
        @close="closeNotificationPreferencesModal"
        @click:primary="handleUpdateDefaultNotificationPreferences"
      >
        <NotificationSettingsView
          v-model="notificationPreferences['default']"
          hide-reset-btn
        />
      </iModal>
    </Teleport>
    <Teleport to="body">
      <iModal
        :show-header="false"
        :show-footer="false"
        :max-width="900"
        :primary-action-button-disabled="!channelName"
        :primary-action-button-loading="isLoadingCreateChannel"
        :show-secondary-action-button="false"
        :visible="showCreateChannelModal"

        @close="showCreateChannelModal = false"
      >
        <iRow
          width="fill"
          :height="500"
          align="between"
          wrap="nowrap"
          gap="standard"
        >
          <iColumn :width="400" height="fill" vertical-align="between">
            <iColumn>
              <iRow width="fill" vertical-align="middle">
                <iButton
                  variant="tertiary"
                  icon-left="ion:chevron-back-outline"
                  :style-overrides="{
                    standardPaddingTop: 'none',
                    standardPaddingBottom: 'none',
                    standardPaddingLeft: 'none',
                    standardPaddingRight: 'none',
                  }"
                  @click="showCreateChannelModal = false"
                >
                  Back
                </iButton>
              </iRow>
              <iSubHeading>Channel Details</iSubHeading>
              <iLabel>Channel Name</iLabel>
              <iTextInput
                v-model="channelName"
                width="fill"
                placeholder="e.g. subscription-budget"

                description="Channels are where conversations happen around a topic. Use a name that is easy to
            find and understand."
                :focus="true"
              />
              <iLabel>Visibility</iLabel>
              <iRadioButton
                v-for="visibilityOption in possibleVisibilityOptions"
                :key="visibilityOption.value"
                v-model="visibility"
                :label="visibilityOption.label"
                :value="visibilityOption.value"
                name="visibility"
              />
            </iColumn>
            <iColumn>
              <iButton
                variant="primary"
                size="small"
                width="fill"
                @click="handleCreateChatroomChannel"
              >
                Create
              </iButton>
            </iColumn>
          </iColumn>
          <iDivider orientation="vertical" />
          <iColumn
            width="fill"
            height="fill"
            wrap="nowrap"
            gap="small"
          >
            <iRow width="fill" align="right" vertical-align="middle">
              <iButton
                variant="tertiary"
                icon-left="material-symbols:close-rounded"
                size="extraSmall"
                @click="showCreateChannelModal = false"
              />
            </iRow>
            <iColumn
              height="fill"
              width="fill"
              align="center"
              vertical-align="middle"
            >
              <iColumn
                height="fill"
                width="fill"
                vertical-align="between"
                :border-left="1"
                :border-bottom="1"
                :border-right="1"
                :border-top="1"
                :style-overrides="{
                  borderRadius: 'large',
                  paddingTop: 'standard',
                  paddingBottom: 'standard',
                  paddingLeft: 'standard',
                  paddingRight: 'standard',
                }"
              >
                <iSubHeading>#{{ channelName }}</iSubHeading>
                <iRow
                  height="hug"
                  width="fill"
                  align="between"
                  wrap="nowrap"
                >
                  <iTextInput disabled />
                  <iButton disabled variant="secondary" icon-left="material-symbols:send-outline-rounded" />
                </iRow>
              </iColumn>
            </iColumn>
          </iColumn>
        </iRow>
      </iModal>
    </Teleport>
  </template>
</template>

<script>
import { useGeneralStore } from "%/stores/general-store";
import { mapActions, mapState } from "pinia";
import {
  ListOfChannelsElementMixin,
} from "~/dynamic-components/standard-elements/list-of-channels/list-of-channels-element-mixin";
import { CHANNEL_CONTEXT, DIRECT_MESSAGE_CONTEXT, useChannelStore } from "~/stores/channel-store";
import { Enum } from "@vcl";
import useFanPublicationStore from "%/stores/fan-publication-store";
import { useConversationStore } from "%/apps/conversations/stores/conversation-store";
import NotificationSettingsView from "~/components/notifications/NotificationSettingsView.vue";
import { useNotificationPreferenceStore } from "~/stores/notifications-store";

export default {
  name: "ChannelList",
  components: { NotificationSettingsView },
  mixins: [
    ListOfChannelsElementMixin,
  ],
  props: {
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    entityId: {
      type: [String, Number],
      required: true,
    },
    textStyleOverrides: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    width: {
      type: [Enum, Number],
      required: false,
      default: "hug",
      options: ["fill", "hug"],
    },
    height: {
      type: [Enum, Number],
      required: false,
      default: "fill",
      options: ["fill", "hug"],
    },
    minHeight: {
      type: [Enum, Number],
      required: false,
      default: "unset",
      options: ["unset"],
    },
    minWidth: {
      type: [Enum, Number],
      required: false,
      default: "unset",
      options: ["unset"],
    },
    maxHeight: {
      type: [Enum, Number],
      required: false,
      default: "unset",
      options: ["unset"],
    },
    maxWidth: {
      type: [Enum, Number],
      required: false,
      default: "unset",
      options: ["unset"],
    },
  },
  data() {
    return {
      isChannelCollapsed: false,
      isDirectMessagesCollapsed: false,
      CHANNEL_CONTEXT,
      DIRECT_MESSAGE_CONTEXT,
      pollingIntervalId: null,
      dmUserDataCache: new Map(),
    };
  },
  computed: {
    ...mapState(useGeneralStore, ["fanUrl", "userData"]),
    ...mapState(useFanPublicationStore, ["publicationData"]),
    ...mapState(useChannelStore, [
      "activeEntityId",
      "activeContext",
      "activeConversationId",
      "channels",
      "directMessages",
      "authorStatuses",
    ]),
    ...mapState(useNotificationPreferenceStore, [
      "notificationPreferences",
      "loadingUpdateNotificationPreferences",
    ]),
    ...mapState(useConversationStore, ["conversationData"]),
    filteredChannels() {
      if (this.isChannelCollapsed) {
        return this.channels.filter(channel => this.isChannelActive(channel));
      }
      return this.channels;
    },
    filteredDirectMessageConversations() {
      if (!this.directMessages) {
        return [];
      }
      if (this.isDirectMessagesCollapsed) {
        return this.directMessages.filter(dm => this.isDirectMessageActive(dm));
      }
      return this.directMessages;
    },
    hasDirectMessages() {
      return this.directMessages && this.directMessages.length > 0;
    },
    unseenMessagesMap() {
      const map = new Map();
      for (const convId in this.conversationData) {
        map.set(parseInt(convId), this.conversationData[convId]?.has_unseen_messages || false);
      }
      return map;
    },
    activeChannelId() {
      if (this.activeContext !== CHANNEL_CONTEXT) {
        return null;
      }
      return parseInt(this.activeEntityId);
    },
    activeDirectMessageConversationId() {
      if (this.activeContext !== DIRECT_MESSAGE_CONTEXT) {
        return null;
      }
      return parseInt(this.activeConversationId);
    },
  },
  watch: {
    directMessages: {
      deep: true,
      handler() {
        this.dmUserDataCache.clear();
      },
    },
  },
  mounted() {
    this.fetchDefaultNotificationPreferences();
  },
  unmounted() {
    this.dmUserDataCache.clear();
  },
  methods: {
    ...mapActions(useNotificationPreferenceStore, [
      "fetchDefaultNotificationPreferences",
      "resetNotificationPreferences",
      "updateDefaultNotificationPreferences",
    ]),

    handleUpdateDefaultNotificationPreferences() {
      this.updateDefaultNotificationPreferences(() => {
        this.showGlobalNotificationSettingsModel = false;
      });
    },

    closeNotificationPreferencesModal() {
      this.showGlobalNotificationSettingsModel = false;
      this.resetNotificationPreferences();
    },

    hasUnseenMessages(conversationId) {
      return this.conversationData[conversationId]?.has_unseen_messages || false;
    },

    hasDmUnseenMessages(dmConversation) {
      const conversationId = this.getDmConversationId(dmConversation);
      return this.hasUnseenMessages(conversationId);
    },

    isChannelActive(channelData) {
      return this.activeContext === CHANNEL_CONTEXT &&
          parseInt(this.activeEntityId) === parseInt(channelData.channel_id);
    },

    isDirectMessageActive(dmConversation) {
      const conversationId = this.getDmConversationId(dmConversation);
      return this.activeContext === DIRECT_MESSAGE_CONTEXT &&
          parseInt(this.activeConversationId) === parseInt(conversationId);
    },

    getDmUserData(dmConversation) {
      const key = this.getDmConversationId(dmConversation);

      if (!this.dmUserDataCache.has(key)) {
        let userData;
        if (dmConversation.participants) {
          userData = this.findOtherParticipant(dmConversation.participants);
        } else {
          userData = dmConversation.user_data || {};
        }
        this.dmUserDataCache.set(key, userData);
      }

      return this.dmUserDataCache.get(key);
    },

    findOtherParticipant(participants) {
      const otherParticipant = participants.find(p => p.user_id !== this.userData.user_id);
      return otherParticipant || participants[0] || {};
    },

    getDmConversationId(dmConversation) {
      return dmConversation.conversation_id
          || dmConversation.conversation?.conversation_id
          || dmConversation.conversation?.conversationData?.conversation_id;
    },

    getDmEntityId(dmConversation) {
      return dmConversation.entity_id || dmConversation.dm_data?.dm_id;
    },

    channelIcon(visibility) {
      return visibility.includes("invite_only") ? "lock" : "hashtag";
    },

    toggleCollapse() {
      this.isChannelCollapsed = !this.isChannelCollapsed;
    },

    toggleDirectMessagesCollapse() {
      this.isDirectMessagesCollapsed = !this.isDirectMessagesCollapsed;
    },

    getCollapseIcon(isCollapsed) {
      return isCollapsed ? "caret-right" : "caret-down";
    },

    getActiveParticipantCount(conversationId) {
      if (!conversationId) {
        return 0;
      }
      return this.conversationData[conversationId]?.active_participants_count || 0;
    },
  },
  styleGuide: () => ({
    activeItemBackgroundColor: { "color.background": "standard" },
    itemHoverBackgroundColor: { "color.background": "subtle" },
    itemPadding: { "size.spacing": "extraSmall" },
    activeItemFontColor: { "color.font": "standard" },
    itemBorderRadius: { "size.borderRadius": "large" },
    infoColor: { "color.font": "subtle" },
    infoSize: { "size.font": "small" },
    chipHeight: { "size.spacing": "standard" },
    chatroomHeaderBorderBottomSize: { "size.border": "thin" },
    chatroomHeaderBorderBottomColor: { "color.border": "standard" },
    chatroomHeaderPaddingTop: { "size.spacing": "none" },
    chatroomHeaderPaddingBottom: { "size.spacing": "extraSmall" },
    chatroomHeaderPaddingLeft: { "size.spacing": "none" },
    chatroomHeaderPaddingRight: { "size.spacing": "none" },
    onlineColor: { "color.font": "success" },
    statusBorderColor: { "color.border": "dark" },
    statusBorderWidth: { "size.border": "thin" },
  }),
};
</script>

<style lang="scss" scoped>
.medium-message-placeholder {
  height: 20px;
  width: 100%;
  background-color: lightgrey;
  margin-bottom: 10px;
  border-radius: 8px;
}

.active {
  color: v-bind('$getStyles.activeItemFontColor');
}

.channel-list-item.active {
  background: v-bind('$getStyles.activeItemBackgroundColor');

  :deep(svg) {
    fill: v-bind('$getStyles.activeItemFontColor');
  }
}

.section-header {
  padding-inline: v-bind('$getStyles.itemPadding');
}

.channel-list-item {
  border-radius: v-bind('$getStyles.itemBorderRadius');
  padding: v-bind('$getStyles.itemPadding');
  cursor: pointer;

  &:hover:not(.active) {
    background: v-bind('$getStyles.itemHoverBackgroundColor');
  }
}

.dm-avatar :deep(img) {
  border-radius: v-bind('$getStyles.borderRadius');
}

.info-text {
  color: v-bind('$getStyles.infoColor');
  font-size: v-bind('$getStyles.infoSize') !important;

  &:hover {
    color: v-bind('$getStyles.infoColor');
    font-size: v-bind('$getStyles.infoSize') !important;
  }
}

.menu-item-link {
  width: 100%;
  padding: 0 !important;
}

:deep(.i-chip) {
  height: v-bind('$getStyles.chipHeight');
}

.status {
  border-radius: 50%;
  width: 6px;
  height: 6px;

  &.online {
    border: v-bind("$getStyles.statusBorderWidth") solid v-bind("$getStyles.onlineColor") !important;
    background-color: v-bind("$getStyles.onlineColor") !important;
  }

  &.offline {
    border: v-bind("$getStyles.statusBorderWidth") solid v-bind("$getStyles.statusBorderColor") !important;
  }
}
</style>
